/**
 * Team Members Hook
 *
 * Loads the list of people the current manager is responsible for, for the
 * "My Team" tab in the Manager Portal.
 *
 * The set of managed personnel numbers comes from `useManagerStatus`
 * (derived from EssWorkerManagerRelations). For each personnel number we fetch
 * the employee record from `/data/EmployeesV2` and their positions from
 * `/data/PositionsV2`, then map them into the card shape the UI expects:
 * { id, name, role, status, personnelNumber }.
 *
 * The `role` (Job Title) is taken from the primary position's Description —
 * the same source the Employment Details tab uses (see useTeamMemberDetail /
 * TeamMemberDetailModal) — so the card and modal stay consistent.
 *
 * Returns:
 *   - members: Array<{ id, name, role, status, personnelNumber }>
 *   - isLoading: boolean
 *   - error: Error | null
 */
import { useQuery } from "react-query";
import { employeeService, positionService } from "../../api/services";
import useManagerStatus from "./useManagerStatus";

/**
 * Pick the primary position from a PositionsV2 result, falling back to the
 * first record. Mirrors PersonalInformation.jsx which reads positionsData[0].
 */
const getPrimaryPosition = (positions = []) => {
  if (!positions.length) return null;
  return (
    positions.find((p) => p.PrimaryPosition === "Yes") || positions[0]
  );
};

/**
 * Map a raw EmployeesV2 record + positions into the card shape used by the
 * My Team grid.
 */
const mapEmployeeToMember = (employee, positions, personnelNumber) => {
  const name =
    employee?.Name ||
    employee?.displayName ||
    employee?.fullName ||
    employee?.DisplayName ||
    personnelNumber;

  // Job Title comes from the primary position's Description — matching the
  // "Job Title" field shown in the Employment Details tab.
  const role = getPrimaryPosition(positions)?.Description || "—";

  // An employee is "On Leave" when the employment has ended / is inactive;
  // otherwise treat as Active. Kept intentionally simple — leave state is
  // surfaced in detail via the leave tabs.
  const isInactive =
    employee?.IsActive === false ||
    employee?.EmploymentStatus === "Inactive" ||
    employee?.WorkerStatus === "Inactive";
  const status = isInactive ? "On Leave" : "Active";

  return {
    id: personnelNumber,
    personnelNumber,
    name,
    role,
    status,
  };
};

export default function useTeamMembers() {
  const {
    managedPersonnelNumbers,
    isLoading: managerStatusLoading,
    error: managerStatusError,
  } = useManagerStatus();

  const {
    data: members,
    isLoading: membersLoading,
    error: membersError,
  } = useQuery(
    ["teamMembers", managedPersonnelNumbers],
    async () => {
      console.log(
        "👥 Fetching team member records for",
        managedPersonnelNumbers.length,
        "personnel number(s)"
      );

      // Fetch each managed employee in parallel; skip any that fail so one bad
      // record doesn't blank the whole list.
      const results = await Promise.all(
        managedPersonnelNumbers.map(async (personnelNumber) => {
          try {
            const [employeeRes, positionsRes] = await Promise.all([
              employeeService.getEmployeeByNumber(personnelNumber),
              positionService.getPositionsByWorker(personnelNumber),
            ]);
            const employee = employeeRes.data?.value?.[0] || null;
            const positions = positionsRes.data?.value || [];
            return mapEmployeeToMember(employee, positions, personnelNumber);
          } catch (err) {
            console.error(
              `❌ Failed to fetch team member ${personnelNumber}:`,
              err
            );
            return null;
          }
        })
      );

      return results.filter(Boolean);
    },
    {
      enabled: managedPersonnelNumbers.length > 0,
      retry: 2,
      staleTime: 5 * 60 * 1000,
    }
  );

  return {
    members: members ?? [],
    isLoading:
      managerStatusLoading ||
      (managedPersonnelNumbers.length > 0 && membersLoading),
    error: managerStatusError || membersError || null,
  };
}
